import type { MeResponse } from "@ondo/shared";

import { useSeatInvitationsController } from "./useSeatInvitationsController.ts";
import { SEAT_INVITATION_COPY } from "./workspace.constants.ts";
import "./workspace.css";

type Props = {
  replaceMe: (me: MeResponse) => void;
};

/**
 * "You have been invited to a firm" — OND-24.
 *
 * Renders what the controller decided and nothing more, and is hidden entirely
 * when nothing is waiting — which is the answer for almost everybody, so the
 * screen that mounts it pays nothing for carrying it.
 *
 * ONE BLOCK PER INVITATION, because two firms may have invited the same person
 * and neither should hide the other. The classes are the claim-studio banner's,
 * reused rather than copied: it is the same kind of thing drawn the same way —
 * a door the account did not know it had.
 */
export function SeatInvitationsBanner({ replaceMe }: Props) {
  const { view, accept, decline } = useSeatInvitationsController({ replaceMe });

  if (view.kind === "hidden") return null;

  return (
    <>
      {view.notes.map((note) => (
        <section className="workspace__claim-studio" key={note.invitationId}>
          <p className="workspace__claim-studio-eyebrow">{SEAT_INVITATION_COPY.eyebrow}</p>
          <p className="workspace__note">{note.sentence}</p>

          {/* The refusal is drawn once, under whichever block a request was in
              flight for. A sentence on both blocks would say the wrong firm had
              refused. */}
          {view.error && view.busy === note.invitationId ? (
            <p className="workspace__error" role="alert">
              {view.error}
            </p>
          ) : null}

          <div className="workspace__claim-studio-actions">
            <button
              className="workspace__apply"
              type="button"
              disabled={view.busy !== null}
              onClick={() => accept(note.invitationId)}
            >
              {view.busy === note.invitationId
                ? SEAT_INVITATION_COPY.accepting
                : SEAT_INVITATION_COPY.accept}
            </button>
            <button
              type="button"
              disabled={view.busy !== null}
              onClick={() => decline(note.invitationId)}
            >
              {view.busy === note.invitationId
                ? SEAT_INVITATION_COPY.declining
                : SEAT_INVITATION_COPY.decline}
            </button>
          </div>
        </section>
      ))}
    </>
  );
}
